import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useMessages } from '../../components/Messages';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { showApiError } from '../../utils/errorUtils';
import './ProductTypes.css';
import { formatAppDate } from '../../utils/dateUtils';

const ViewProductType = forwardRef(({ productTypeId, onBack, editMode = false, createMode = false, onSave, onCancel, onEdit }, ref) => {
  const { t } = useTranslation('production');
  const { t: tCommon } = useTranslation('common');
  const [productType, setProductType] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [hasChanges, setHasChanges] = useState(false);
  const [workflows, setWorkflows] = useState([]);

  const { showSuccess, showError, showWarning } = useMessages();

  useImperativeHandle(ref, () => ({
    handleDelete,
    handleSave
  }));

  useEffect(() => {
    fetchWorkflows();
    if (productTypeId && !createMode) {
      fetchProductType();
    } else if (createMode) {
      const newProductType = {
        type_name: '',
        description: '',
        default_workflow_id: null,
        is_active: true
      };
      setProductType(newProductType);
      setEditData(newProductType);
    }
  }, [productTypeId, createMode]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (editMode || createMode) {
          handleCancel();
        } else {
          if (onBack) onBack();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editMode, createMode, onBack]);

  const fetchWorkflows = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/workflows/'));
      if (response.ok) {
        const data = await response.json();
        setWorkflows(data.workflows || data || []);
      } else {
        console.error('Failed to fetch workflows');
      }
    } catch (error) {
      console.error('Error fetching workflows:', error);
    }
  };

  const fetchProductType = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/product-types/${productTypeId}`));
      if (response.ok) {
        const data = await response.json();
        setProductType(data);
        setEditData(data);
      } else {
        setError(t('productTypes.view.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching product type:', error);
      setError(t('productTypes.view.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setError(null);

      // Validate required fields
      if (!editData.type_name || !editData.type_name.trim()) {
        showError(t('productTypes.view.messages.validationError'), t('productTypes.view.messages.typeNameRequired'));
        return;
      }

      if (createMode && !editData.default_workflow_id) {
        showError(t('productTypes.view.messages.validationError'), t('productTypes.view.messages.workflowRequired'));
        return;
      }

      const url = createMode
        ? getApiUrl('/api/product-types/')
        : getApiUrl(`/api/product-types/${productTypeId}`);

      const method = createMode ? 'POST' : 'PUT';

      // Sanitize data: convert empty strings to null for optional integer fields
      const sanitizedData = {
        ...editData,
        default_workflow_id: editData.default_workflow_id || null,
        description: editData.description || null
      };

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sanitizedData)
      });

      if (response.ok) {
        const result = await response.json();
        setHasChanges(false);

        showSuccess(
          createMode ? t('productTypes.view.messages.productTypeCreated') : t('productTypes.view.messages.productTypeUpdated'),
          createMode ? t('productTypes.view.messages.productTypeCreatedSuccess') : t('productTypes.view.messages.productTypeUpdatedSuccess')
        );

        if (createMode && result.product_type_id) {
          setProductType(result);
          setEditData(result);
        } else if (!createMode) {
          // Update local state immediately with the result
          setProductType(result);
          setEditData(result);
          // Then fetch to ensure consistency
          await fetchProductType();
        }

        if (onSave) onSave(result);
      } else {
        const parsed = await showApiError(response, showError, tCommon);
        setError(parsed.message);
        return;
      }
    } catch (error) {
      console.error('Error saving product type:', error);
      setError(t('productTypes.view.error.savingError'));
      showError(
        createMode ? t('productTypes.view.messages.failedToCreateProductType') : t('productTypes.view.messages.failedToUpdateProductType'),
        t('productTypes.view.error.savingError')
      );
    }
  };

  const handleCancel = () => {
    if (createMode) {
      if (onCancel) onCancel();
    } else {
      setEditData(productType);
      setHasChanges(false);
      if (onCancel) onCancel();
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('productTypes.view.messages.deleteConfirmation'))) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/product-types/${productTypeId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        showSuccess(t('productTypes.view.messages.productTypeDeleted'), t('productTypes.view.messages.productTypeDeletedSuccess'));
        if (onBack) onBack();
      } else {
        const parsed = await showApiError(response, showError, tCommon);
        setError(parsed.message);
      }
    } catch (error) {
      console.error('Error deleting product type:', error);
      setError(t('productTypes.view.error.deletingError'));
      showError(t('productTypes.view.messages.failedToDeleteProductType'), t('productTypes.view.error.deletingError'));
    }
  };

  const renderOverviewTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('productTypes.view.sections.productTypeInformation')}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('productTypes.view.fields.typeName')} *</label>
            <input
              type="text"
              value={editData.type_name || ''}
              onChange={(e) => handleFieldChange('type_name', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('productTypes.view.placeholders.typeName')}
              required
            />
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={editData.is_active || false}
                onChange={(e) => handleFieldChange('is_active', e.target.checked)}
                disabled={!editMode && !createMode}
              />
              <span>{t('productTypes.view.fields.active')}</span>
            </label>
          </div>
        </div>

        {!editMode && !createMode && productType && (
          <div className="form-section">
            <h3>{t('productTypes.view.sections.workflow')}</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>{t('productTypes.view.fields.workflowName')}</label>
                <input
                  type="text"
                  value={productType.workflow_name || t('productTypes.view.defaultValues.noWorkflow')}
                  disabled
                />
              </div>
              <div className="form-group full-width">
                <label>{t('productTypes.view.fields.workflowDescription')}</label>
                <textarea
                  value={productType.workflow_description || t('productTypes.view.defaultValues.noDescriptionAvailable')}
                  disabled
                  rows="2"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderDetailsTab = () => (
    <div className="tab-content">
      <div className="form-section">
        <h3>{t('productTypes.view.sections.additionalInformation')}</h3>
        <div className="form-grid">
          <div className="form-group full-width">
            <label>{t('productTypes.view.fields.description')}</label>
            <textarea
              value={editData.description || ''}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              disabled={!editMode && !createMode}
              placeholder={t('productTypes.view.placeholders.description')}
              rows="4"
            />
          </div>
          <div className="form-group">
            <label>{t('productTypes.view.fields.workflow')} *</label>
            <select
              value={editData.default_workflow_id || ''}
              onChange={(e) => handleFieldChange('default_workflow_id', e.target.value ? parseInt(e.target.value) : null)}
              disabled={!editMode && !createMode}
              required
            >
              <option value="" disabled hidden>{t('productTypes.view.placeholders.selectWorkflow')}</option>
              {/* Inactive workflows are retired; only the one already saved on the type stays visible */}
              {workflows
                .filter(workflow => workflow.is_active || workflow.workflow_id === productType?.default_workflow_id)
                .map(workflow => (
                  <option key={workflow.workflow_id} value={workflow.workflow_id}>
                    {workflow.workflow_name}{workflow.is_active ? '' : ` (${t('productTypes.view.status.inactive')})`}
                  </option>
                ))}
            </select>
          </div>
        </div>
      </div>

      {!createMode && (
        <div className="form-section">
          <h3>{t('productTypes.view.sections.systemInformation')}</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>{t('productTypes.view.fields.createdDate')}</label>
              <input
                type="text"
                value={productType?.created_date ? formatAppDate(productType.created_date, 'datetime') : t('productTypes.view.defaultValues.notAvailable')}
                disabled
              />
            </div>
            <div className="form-group">
              <label>{t('productTypes.view.fields.lastUpdated')}</label>
              <input
                type="text"
                value={productType?.updated_date ? formatAppDate(productType.updated_date, 'datetime') : t('productTypes.view.defaultValues.never')}
                disabled
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return <div className="view-container"><div className="loading">{t('productTypes.view.loading')}</div></div>;
  }

  if (error) {
    return <div className="view-container"><div className="error-message">{error}</div></div>;
  }

  if (!productType && !createMode) {
    return <div className="view-container"><div className="loading">{t('productTypes.view.noProductTypeFound')}</div></div>;
  }

  return (
    <div className="view-container">
      <div className="view-header">
        <div className="header-title">
          <button className="btn-back" onClick={onBack}>←</button>
          <h1>
            {createMode ? t('productTypes.view.titles.newProductType') : editData.type_name || t('productTypes.view.titles.productType')}
          </h1>
          {!createMode && editData.is_active !== undefined && (
            <span className={`status-badge ${editData.is_active ? 'status-active' : 'status-inactive'}`}>
              {editData.is_active ? t('productTypes.view.status.active') : t('productTypes.view.status.inactive')}
            </span>
          )}
        </div>
        <div className="header-actions">
          {!createMode && !editMode && (
            <>
              <button className="btn-secondary" onClick={onEdit}>{t('productTypes.view.buttons.edit')}</button>
            </>
          )}
          {(createMode || editMode) && (
            <>
              <button className="btn-secondary" onClick={handleCancel}>{t('productTypes.view.buttons.cancel')}</button>
              <button className="btn-primary" onClick={handleSave}>
                {createMode ? t('productTypes.view.buttons.create') : t('productTypes.view.buttons.save')}
              </button>
            </>
          )}
        </div>
      </div>
      <div className="tabs">
        <div className="tab-list">
          <button
            className={`tab-item ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            📋 {t('productTypes.view.tabs.overview')}
          </button>
          <button
            className={`tab-item ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            📝 {t('productTypes.view.tabs.details')}
          </button>
        </div>

        <div className="tab-panels">
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'details' && renderDetailsTab()}
        </div>
      </div>
    </div>
  );
});

ViewProductType.displayName = 'ViewProductType';

export default ViewProductType;